/**
 * Chart arrangements, where the screener sits beside them, and applying both to
 * a layout.
 *
 * A template says only where the *charts* go. Everything else in a layout — a
 * screener, later a blotter — takes a whole track of its own beside the chart
 * region: a column on the left or right, a row on the top or bottom. That rule
 * is what lets `Default` (chart + screener) be "1 chart", which is what it is:
 * the screener is not one of the things you are choosing between when you pick
 * an arrangement, so its side is a separate choice over the same geometry.
 *
 * **The side is derived, never stored.** `Section.row`/`column` already say
 * where the screener is, so a `placement` field on `Layout` would be a second
 * source of truth that the first divider drag falsifies — the same argument
 * {@link matchTemplate} makes about a stored template id, and the same answer:
 * read it back out of the geometry ({@link screenerPlacement}).
 *
 * **Applying a template is a rearrangement, not a reset.** Existing charts keep
 * their symbol, interval, panes and plots; only their cell changes. Losing four
 * configured charts because you tried a different grid is not the kind of thing
 * people forgive, so the surviving charts are matched to cells in reading order
 * and only the surplus is dropped — and `chartsDropped` exists so the caller can
 * ask before doing it. Changing the screener's side keeps the arrangement for
 * the same reason: it is one rearrangement expressed two ways, not two features.
 *
 * Counts stop at 6 deliberately. Every chart is a WebGL context and a data
 * subscription, which is a real cost a canvas-drawing library does not pay, and
 * the arrangements past six exist in other products because someone once asked
 * rather than because they are used. Nothing here would need to change to add
 * them — a template is data.
 */

import type { Layout, Section } from "@qufolio/model";

export interface TemplateCell {
  row: number;
  column: number;
  rowSpan: number;
  columnSpan: number;
}

export interface LayoutTemplate {
  id: string;
  /** Named in words, because a row of 16px glyphs differing by one line is a puzzle. */
  label: string;
  /** Rows and columns of the *chart region* only. */
  rows: number;
  columns: number;
  cells: TemplateCell[];
}

const cell = (row: number, column: number, rowSpan = 1, columnSpan = 1): TemplateCell => ({
  row,
  column,
  rowSpan,
  columnSpan,
});

/** A plain `rows × columns` grid, one chart per cell, in reading order. */
function grid(id: string, label: string, rows: number, columns: number): LayoutTemplate {
  const cells: TemplateCell[] = [];
  for (let r = 0; r < rows; r++) for (let c = 0; c < columns; c++) cells.push(cell(r, c));
  return { id, label, rows, columns, cells };
}

export const LAYOUT_TEMPLATES: readonly LayoutTemplate[] = [
  grid("1", "Single", 1, 1),
  grid("2-across", "2 across", 1, 2),
  grid("2-stacked", "2 stacked", 2, 1),
  grid("3-across", "3 across", 1, 3),
  grid("3-stacked", "3 stacked", 3, 1),
  // One wide chart over two — the shape you want when one chart is the subject
  // and the others are context.
  {
    id: "1-2",
    label: "1 + 2",
    rows: 2,
    columns: 2,
    cells: [cell(0, 0, 1, 2), cell(1, 0), cell(1, 1)],
  },
  grid("2x2", "2 × 2", 2, 2),
  {
    id: "1-3",
    label: "1 + 3",
    rows: 2,
    columns: 3,
    cells: [cell(0, 0, 1, 3), cell(1, 0), cell(1, 1), cell(1, 2)],
  },
  grid("3x2", "3 × 2", 2, 3),
  // Five side by side, one row (owner, 2026-10-06): the index wall — SPY, SPX,
  // RUT, a sector, a theme — read across at one height, each chart tall enough
  // for a stack of indicator panes under it. 5 × 2 halves that height.
  grid("5-across", "5 across", 1, 5),
  // ── past six, the designer's shelf ───────────────────────────────────────
  //
  // Not in the popover: it shows counts up to four, in one row. These are what
  // the designer offers, and the split is a filter on `templatesByCount` rather
  // than a second list — one shelf, two views of it.
  //
  // **Every one is a plain grid, and that is deliberate.** An asymmetric shape
  // at this size (1 + 11, say) is a layout somebody wants *once*, and guessing
  // which is what the editor exists for. The grids are the shapes that are
  // obviously worth having: a wall of charts, in the aspect the screen is.
  grid("2x3", "2 × 3", 3, 2),
  grid("4x2", "4 × 2", 2, 4),
  grid("2x4", "2 × 4", 4, 2),
  grid("3x3", "3 × 3", 3, 3),
  grid("5x2", "5 × 2", 2, 5),
  grid("4x3", "4 × 3", 3, 4),
  grid("4x4", "4 × 4", 4, 4),
];

/**
 * The most charts the popover offers, and therefore the designer's floor.
 *
 * Four, because the row of tiles is measured: eight tiles at 48px with 6px gaps
 * is 426px plus padding, and the panel has to stay a popover rather than become
 * a window. Everything past it is one click away.
 */
export const POPOVER_MAX_CHARTS = 4;

/**
 * The arrangements the layout panel shows, in its own order: every one, by
 * chart count (owner's call, 2026-10-03). The designer that was to hold
 * everything past {@link POPOVER_MAX_CHARTS} was never built, so the grids past
 * four were unreachable; the panel widened to five tiles a row to take them.
 */
export function popoverTemplates(): LayoutTemplate[] {
  return templatesByCount().flatMap(({ templates }) => templates);
}

/** How many charts a template holds. */
export const templateCount = (template: LayoutTemplate): number => template.cells.length;

/** Templates grouped by chart count, ascending — the panel's own order. */
export function templatesByCount(): Array<{ count: number; templates: LayoutTemplate[] }> {
  const byCount = new Map<number, LayoutTemplate[]>();
  for (const template of LAYOUT_TEMPLATES) {
    const count = templateCount(template);
    byCount.set(count, [...(byCount.get(count) ?? []), template]);
  }
  return [...byCount.entries()]
    .sort(([a], [b]) => a - b)
    .map(([count, templates]) => ({ count, templates }));
}

/** The chart sections of a layout, in reading order. */
export function chartsOf(layout: Layout): Section[] {
  return layout.sections
    .filter((s) => s.kind === "chartGroup")
    .sort((a, b) => a.row - b.row || a.column - b.column);
}

/**
 * The grid's cells, in reading order: charts **and data cells**
 * (`lib/data-layout.ts`). The arrangement is of these; `chartsOf` stays for
 * what is about charts only — the count in ✎'s tooltip, the symbol lock.
 *
 * Without this a data cell was an "other" — a side track like the screener —
 * and applying 3×3 to a data layout stacked all nine down the side.
 */
export function cellsOf(layout: Layout): Section[] {
  return layout.sections
    .filter((s) => s.kind === "chartGroup" || s.kind === "dataView")
    .sort((a, b) => a.row - b.row || a.column - b.column);
}

/** Everything that is not a cell — the screener's track — in declared order. */
function othersOf(layout: Layout): Section[] {
  return layout.sections.filter((s) => s.kind !== "chartGroup" && s.kind !== "dataView");
}

/** Which side of the chart region the screener is docked on. */
export type ScreenerPlacement = "right" | "left" | "top" | "bottom";

/** The four sides, in the order the panel offers them. */
export const SCREENER_PLACEMENTS: ReadonlyArray<{
  id: ScreenerPlacement;
  label: string;
}> = [
  { id: "left", label: "Left" },
  { id: "right", label: "Right" },
  { id: "top", label: "Top" },
  { id: "bottom", label: "Bottom" },
];

/** `top` and `bottom` give the screener a row; `left` and `right` a column. */
export const placementIsVertical = (placement: ScreenerPlacement): boolean =>
  placement === "top" || placement === "bottom";

/** The half-open row and column ranges a set of sections covers, or nothing. */
interface Box {
  row: number;
  rowEnd: number;
  column: number;
  columnEnd: number;
}

function boxOf(sections: readonly Section[]): Box | undefined {
  if (!sections.length) return undefined;
  return {
    row: Math.min(...sections.map((s) => s.row)),
    rowEnd: Math.max(...sections.map((s) => s.row + (s.rowSpan ?? 1))),
    column: Math.min(...sections.map((s) => s.column)),
    columnEnd: Math.max(...sections.map((s) => s.column + (s.columnSpan ?? 1))),
  };
}

/**
 * Which side the screener is on, read out of the grid rather than stored.
 *
 * `Layout` has no `screenerPlacement` field and must not grow one: "screener on
 * the left" is a different arrangement of `row`/`column`/`rowSpan`/`columnSpan`
 * over `rows`/`columns`, all of which already exist and all of which a divider
 * drag or a hand-built layout can change without going through this module. A
 * stored side would be a second source of truth, and the first thing it would
 * disagree with is the grid the user is looking at.
 *
 * Compared as bounding boxes, so a screener beside a 2×2 of charts is answered
 * the same way as one beside a single chart. Columns are tested before rows
 * because the two can only both be true of a layout where the boxes do not
 * touch at all, which no arrangement here produces.
 *
 * `right` is the answer when there is nothing to compare — no screener, no
 * chart, or a hand-built layout whose boxes overlap. It is the shipped default,
 * so a layout nothing can classify keeps looking like the one people know.
 */
export function screenerPlacement(layout: Layout): ScreenerPlacement {
  const charts = boxOf(cellsOf(layout));
  const others = boxOf(othersOf(layout));
  if (!charts || !others) return "right";
  if (others.column >= charts.columnEnd) return "right";
  if (others.columnEnd <= charts.column) return "left";
  if (others.rowEnd <= charts.row) return "top";
  if (others.row >= charts.rowEnd) return "bottom";
  return "right";
}

/** How many configured charts applying this template would destroy. */
export function chartsDropped(layout: Layout, template: LayoutTemplate): number {
  return Math.max(0, cellsOf(layout).length - templateCount(template));
}

/**
 * Which template a layout currently matches, if any.
 *
 * Matched on the chart cells rather than on a stored id, because the contract
 * has no field for "which template this was" and should not grow one: a layout
 * *is* its geometry, and a stored id would be a second source of truth that a
 * divider drag could falsify.
 *
 * Cells are compared **relative to the chart region's own origin**, not to the
 * grid's. With the screener on the left or on top the charts start at row or
 * column 1, and an absolute comparison would report that a layout the panel had
 * just applied matched nothing — the arrangement would silently untick itself
 * the moment the screener moved.
 */
export function matchTemplate(layout: Layout): LayoutTemplate | undefined {
  const charts = cellsOf(layout);
  if (!charts.length) return undefined;
  const origin = boxOf(charts)!;
  return LAYOUT_TEMPLATES.find((template) => {
    if (templateCount(template) !== charts.length) return false;
    return template.cells.every((c, i) => {
      const s = charts[i]!;
      return (
        s.row - origin.row === c.row &&
        s.column - origin.column === c.column &&
        (s.rowSpan ?? 1) === c.rowSpan &&
        (s.columnSpan ?? 1) === c.columnSpan
      );
    });
  });
}

/**
 * The arrangement a layout's charts already make, as a template.
 *
 * What lets the screener be moved without naming an arrangement: whatever shape
 * the charts are in — one of the nine, or something dragged into place by hand —
 * is re-expressed as a template so the one `applyTemplate` code path can lay it
 * out again on the other side. Deriving it beats reaching for
 * `matchTemplate(layout) ?? SINGLE`, which would silently reshape a hand-built
 * layout into a single chart just because someone moved a panel.
 */
function currentArrangement(layout: Layout): LayoutTemplate {
  const charts = cellsOf(layout);
  const origin = boxOf(charts)!;
  const cells = charts.map((s) =>
    cell(s.row - origin.row, s.column - origin.column, s.rowSpan ?? 1, s.columnSpan ?? 1),
  );
  return {
    id: "current",
    label: "Current",
    rows: origin.rowEnd - origin.row,
    columns: origin.columnEnd - origin.column,
    cells,
  };
}

/** `"1.5fr"` → 1.5. Anything else — `300px`, `auto`, absent — is not a share. */
function parseFr(size: string | undefined): number | null {
  const match = /^([0-9]*\.?[0-9]+)fr$/.exec((size ?? "").trim());
  return match ? Number(match[1]) : null;
}

const fr = (n: number): string => `${Number(n.toFixed(4))}fr`;

/**
 * Apply an arrangement, keeping every chart it has room for.
 *
 * `makeChart` mints the sections for cells that have no chart yet. It is passed
 * in rather than imported so this stays a pure function of its arguments — id
 * minting is the one thing that would make the result untestable.
 *
 * `placement` defaults to the side the screener is *already* on, so picking an
 * arrangement rearranges the charts and moves nothing else. Passing it is how
 * {@link placeScreener} says "same charts, other side".
 */
export function applyTemplate(
  layout: Layout,
  template: LayoutTemplate,
  makeChart: (index: number) => Section,
  placement: ScreenerPlacement = screenerPlacement(layout),
): Layout {
  const existing = cellsOf(layout);
  const others = othersOf(layout);
  // A data-only layout grows empty data cells, not charts: "Pick a view" (owner's mock).
  const dataOnly = existing.length > 0 && existing.every((s) => s.kind === "dataView");
  const makeCell = (i: number): Section =>
    dataOnly ? { ...makeChart(i), kind: "dataView", config: {} } : makeChart(i);
  const vertical = placementIsVertical(placement);

  // The chart region starts past the non-chart tracks when they lead it, so
  // `left` and `top` are the two that push the charts off the grid's origin.
  const rowOffset = placement === "top" ? others.length : 0;
  const columnOffset = placement === "left" ? others.length : 0;

  const charts = template.cells.map((c, i) => {
    const base = existing[i] ?? makeCell(i);
    return {
      ...base,
      row: c.row + rowOffset,
      column: c.column + columnOffset,
      rowSpan: c.rowSpan,
      columnSpan: c.columnSpan,
    };
  });

  // Non-chart sections take one whole track each beside the chart region — a
  // full-height column left or right of it, a full-width row above or below. A
  // screener beside a 2×2 of charts is still one screener.
  const sections = [
    ...charts,
    ...others.map((s, i) =>
      vertical
        ? {
            ...s,
            row: placement === "top" ? i : template.rows + i,
            column: 0,
            rowSpan: 1,
            columnSpan: template.columns,
          }
        : {
            ...s,
            row: 0,
            column: placement === "left" ? i : template.columns + i,
            rowSpan: template.rows,
            columnSpan: 1,
          },
    ),
  ];

  return {
    ...layout,
    rows: vertical ? template.rows + others.length : template.rows,
    columns: vertical ? template.columns : template.columns + others.length,
    ...nextSizes(layout, template, others, placement),
    sections,
  };
}

/**
 * Move the screener to another side, keeping the charts exactly as they are.
 *
 * Routed through {@link applyTemplate} over the layout's own arrangement rather
 * than given its own geometry code: the two would then each have an opinion
 * about where a non-chart section goes, and they would drift the first time one
 * of them learned about a second screener.
 *
 * A layout with no charts is returned untouched — there is no chart region for
 * the screener to be on a side *of*, so no placement is expressible.
 */
/**
 * Add a screener to a layout that has none, on the given side.
 *
 * `placeScreener` below *moves* the section it finds and does nothing at all
 * when there is none, which is why a two-chart layout could not gain one: every
 * control that reaches a screener was gated on one already existing. This is the
 * one entry point that creates.
 *
 * A no-op when the layout already has a non-chart section, rather than adding a
 * second: two screeners in one layout is not a shape any arrangement describes,
 * and the caller that wanted to *move* the existing one already has
 * `placeScreener`.
 *
 * Placement runs through the same `applyTemplate` as everything else, so the
 * charts keep their configuration and the new track takes its share of the axis
 * it divides — a screener added to the right is a quarter of the width for the
 * same reason one dragged there stays a quarter.
 */
export function addScreener(
  layout: Layout,
  section: Section,
  placement: ScreenerPlacement = "right",
): Layout {
  if (othersOf(layout).length) return layout;
  return placeScreener({ ...layout, sections: [...layout.sections, section] }, placement);
}

export function placeScreener(layout: Layout, placement: ScreenerPlacement): Layout {
  const charts = cellsOf(layout);
  if (!charts.length) return layout;
  return applyTemplate(
    layout,
    currentArrangement(layout),
    // Unreachable: `currentArrangement` emits exactly one cell per existing
    // chart, so no cell is ever short of one.
    (index) => charts[index]!,
    placement,
  );
}

/**
 * Row and column sizes after a rearrangement: the chart region keeps the share
 * it had, split evenly, and each non-chart track keeps its own size.
 *
 * The proportion is what matters — a screener that was a quarter of the width
 * should still be a quarter after the charts beside it are split in two, not
 * suddenly a third; and a quarter of the width becomes a quarter of the *height*
 * when it moves to the top, because a share of the axis it divides is the only
 * part of "1fr" that survives being transposed. Anything not expressed in `fr`
 * (a pixel width, an absent size) has no share to divide, so the charts fall
 * back to one each and the track keeps its literal value.
 *
 * Only the axis the screener divides carries shares; the other is reset to an
 * even `1fr` per track, as the column-only version always did to `rowSizes`.
 */
function nextSizes(
  layout: Layout,
  template: LayoutTemplate,
  others: Section[],
  placement: ScreenerPlacement,
): { rowSizes: string[]; columnSizes: string[] } {
  const to = placementIsVertical(placement);
  // Read the shares off whichever axis they are on *now* — a screener that is
  // about to become a row was a column a moment ago, and its width is the only
  // record of how much of the layout it had.
  const from = placementIsVertical(screenerPlacement(layout));
  const previous = (from ? layout.rowSizes : layout.columnSizes) ?? [];

  let chartShare = 0;
  for (const track of chartTracks(layout, from)) {
    const share = parseFr(previous[track]);
    if (share === null) {
      chartShare = 0;
      break;
    }
    chartShare += share;
  }

  const chartTrackCount = to ? template.rows : template.columns;
  const perChart = chartShare > 0 ? chartShare / chartTrackCount : 1;
  const chartSizes = Array.from({ length: chartTrackCount }, () => fr(perChart));
  const otherSizes = others.map((s) => previous[from ? s.row : s.column] ?? "1fr");

  const along =
    placement === "left" || placement === "top"
      ? [...otherSizes, ...chartSizes]
      : [...chartSizes, ...otherSizes];
  const across = Array.from({ length: to ? template.columns : template.rows }, () => "1fr");

  return to ? { rowSizes: along, columnSizes: across } : { rowSizes: across, columnSizes: along };
}

/** Every row (or column) index any chart covers, spans included. */
function chartTracks(layout: Layout, vertical: boolean): number[] {
  const tracks = new Set<number>();
  for (const s of cellsOf(layout)) {
    const start = vertical ? s.row : s.column;
    const span = (vertical ? s.rowSpan : s.columnSpan) ?? 1;
    for (let t = start; t < start + span; t++) tracks.add(t);
  }
  return [...tracks];
}
